import {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    type ReactNode,
    type Dispatch,
    type SetStateAction,
} from "react";

import { useWebsiteQueryBarItems } from "app/modules/project-from-comparison/hooks";
import type { CreationErrors } from "app/modules/project-from-comparison/types";
import { ProjectErrors } from "app/modules/project-from-comparison/types";

type ProjectCreationContextValue = {
    domains: string[];
    projectName: string;
    isWeeklyUpdatesEnabled: boolean;
    isCreationEnabled: boolean;
    errors: CreationErrors;
    setErrors: Dispatch<SetStateAction<CreationErrors>>;
    setDomains: Dispatch<SetStateAction<string[]>>;
    setProjectName: Dispatch<SetStateAction<string>>;
    setWeeklyUpdates: Dispatch<SetStateAction<boolean>>;
    resetContext: () => void;
};

const ProjectCreationContext = createContext<ProjectCreationContextValue | null>(null);

type ProjectCreationProviderProps = {
    children: ReactNode;
};

const ProjectCreationProvider = ({ children }: ProjectCreationProviderProps) => {
    const items = useWebsiteQueryBarItems();
    const initialDomains = useMemo(() => items?.map(({ name }) => name) ?? [], [items]);

    const [domains, setDomains] = useState<string[]>(initialDomains);
    const [projectName, setProjectName] = useState<string>("");
    const [errors, setErrors] = useState<CreationErrors>({
        [ProjectErrors.Name]: null,
        [ProjectErrors.SameCompetitors]: false,
    });
    const [isWeeklyUpdatesEnabled, setWeeklyUpdates] = useState(true);

    const isCreationEnabled = useMemo(
        () => !Object.values(errors).filter(Boolean).length && domains.length > 1,
        [domains.length, errors],
    );

    const resetContext = useCallback(() => {
        setDomains(initialDomains);
        setProjectName("");
        setWeeklyUpdates(true);
    }, [initialDomains]);

    const value = useMemo(
        () => ({
            domains,
            projectName,
            isWeeklyUpdatesEnabled,
            isCreationEnabled,
            errors,
            setErrors,
            setDomains,
            setProjectName,
            setWeeklyUpdates,
            resetContext,
        }),

        [domains, projectName, isWeeklyUpdatesEnabled, isCreationEnabled, errors, resetContext],
    );

    return <ProjectCreationContext.Provider value={value}>{children}</ProjectCreationContext.Provider>;
};
export default ProjectCreationProvider;

export const useProjectCreation = (): ProjectCreationContextValue => {
    const context = useContext(ProjectCreationContext);

    if (!context) {
        throw new Error("useProjectCreation must be used within ProjectCreationProvider");
    }

    return context;
};
